import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { ReactComponent as ChevronRight } from '../../../assets/images/svg/chevron-right.svg';
import { ReactComponent as InfoIcon } from '../../../assets/images/svg/info-filled-blue.svg';
import { pxToRem } from '../../../assets/styles/helper';
import themeColors from '../../../assets/styles/themeColors';
import { variables } from '../../../assets/styles/variables';
import Button from '../../Button/Button';
import ModalBody from '../../Modal/ModalBody';
import SectionLoader from '../../Loader/SectionLoader';

const MOCK_LOCATIONS = [
  {
    name: 'FedEx Ship Center',
    address: '47 W 13th St, New York, NY 10011, USA',
  },
  {
    name: 'FedEx Store #1102',
    address: '47 W 13th St, New York, NY 10011, USA',
  },
  {
    name: 'FedEx Ship Center',
    address: '47 W 13th St, New York, NY 10011, USA',
  },
  {
    name: 'FedEx Ship Center',
    address: '47 W 13th St, New York, NY 10011, USA',
  },
  {
    name: 'FedEx Store #1102',
    address: '47 W 13th St, New York, NY 10011, USA',
  },
  {
    name: 'FedEx Ship Center',
    address: '47 W 13th St, New York, NY 10011, USA',
  },
];

// TODO(SFH FedEx locator endpoint): replace with a real action that hits the
// backend / FedEx Locator API. Keep the same async signature so the screen
// component doesn't change.
const searchFedexLocations = async (zip) => {
  await new Promise((resolve) => {
    setTimeout(resolve, 300);
  });
  // TODO(remove): test hook — `90210` returns no results so the empty state
  // renders. Drop this branch once the real endpoint is wired.
  if (zip === '90210') return [];
  return MOCK_LOCATIONS;
};

const Title = styled.div`
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: ${pxToRem(16)};
  font-weight: 700;
  color: ${themeColors.colorTextPrimary};
  padding-top: ${pxToRem(8)};
`;

const Body = styled.div`
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: ${pxToRem(14)};
  color: ${themeColors.colorTextSecondary};
  line-height: 1.5;
  margin-top: ${pxToRem(4)};
`;

const ToggleRow = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  background: none;
  border: none;
  border-bottom: 1px solid ${themeColors.colorBorderPrimary};
  padding: ${pxToRem(16)} 0;
  margin-top: ${pxToRem(20)};
  cursor: pointer;
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: ${pxToRem(14)};
  font-weight: 600;
  color: ${themeColors.colorPrimary};
  text-decoration: underline;

  > svg {
    width: ${pxToRem(16)};
    height: ${pxToRem(16)};
    color: ${themeColors.colorPrimary};
    transition: transform 0.2s ease;
    transform: rotate(${({ $expanded }) => ($expanded ? '-90deg' : '0deg')});
  }
`;

const SearchSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${pxToRem(6)};
  margin-top: ${pxToRem(12)};
`;

const ZipLabel = styled.label`
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: ${pxToRem(13)};
  color: ${themeColors.colorTextPrimary};
`;

const SearchRow = styled.div`
  display: flex;
  gap: ${pxToRem(8)};
`;

const ZipInput = styled.input`
  flex: 1;
  min-width: 0;
  padding: ${pxToRem(10)} ${pxToRem(12)};
  border: 1px solid ${themeColors.colorBorderPrimary};
  border-radius: ${pxToRem(6)};
  background: ${themeColors.colorBackgroundSecondary};
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: ${pxToRem(14)};
  color: ${themeColors.colorTextPrimary};

  &:focus {
    outline: none;
    border-color: ${themeColors.colorPrimary};
  }
`;

const SearchButton = styled.button`
  padding: ${pxToRem(10)} ${pxToRem(20)};
  border: 1px solid ${themeColors.colorPrimary};
  border-radius: ${pxToRem(6)};
  background: ${themeColors.colorWhite};
  color: ${themeColors.colorPrimary};
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: ${pxToRem(14)};
  font-weight: 700;
  cursor: pointer;

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const ResultsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${pxToRem(8)};
  margin-top: ${pxToRem(12)};
  max-height: ${pxToRem(180)};
  overflow-y: auto;
  padding-right: ${pxToRem(4)};
`;

const LocationRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${pxToRem(2)};
  background: ${themeColors.colorBackgroundSecondary};
  border-radius: ${pxToRem(6)};
  padding: ${pxToRem(12)} ${pxToRem(16)};
`;

const LocationName = styled.div`
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: ${pxToRem(14)};
  font-weight: 700;
  color: ${themeColors.colorTextPrimary};
`;

const LocationAddress = styled.div`
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: ${pxToRem(13)};
  color: ${themeColors.colorTextSecondary};
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${pxToRem(12)};
  margin-top: ${pxToRem(40)};
  padding: 0 ${pxToRem(24)};
  text-align: center;

  > svg {
    width: ${pxToRem(36)};
    height: ${pxToRem(36)};
  }
`;

const EmptyText = styled.div`
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: ${pxToRem(14)};
  color: ${themeColors.colorTextSecondary};
  line-height: 1.5;
`;

const NeedHelpLink = styled.a`
  display: inline-block;
  margin-top: ${pxToRem(4)};
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: ${pxToRem(14)};
  color: ${themeColors.colorPrimary};
  text-decoration: underline;
`;

const Spacer = styled.div`
  flex: 1;
  min-height: ${pxToRem(16)};
`;

const Footer = styled.div`
  margin-top: ${pxToRem(16)};

  > button {
    width: 100%;
  }
`;

const SellFromHomeShipping = () => {
  const { t } = useTranslation();
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [zipCode, setZipCode] = useState('');
  const [locations, setLocations] = useState(null);
  const [isSearching, setIsSearching] = useState(false);

  const handleZipChange = (e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 5);
    setZipCode(value);
  };

  const handleSearch = async () => {
    if (zipCode.length !== 5) return;
    setIsSearching(true);
    const result = await searchFedexLocations(zipCode);
    setLocations(result);
    setIsSearching(false);
  };

  const handleContinue = () => {
    // TODO(SFH submit endpoint): wire up once the review/submit endpoint lands.
  };

  const supportPhone = t('sellWizard.sfhWizard.phone');

  return (
    <SectionLoader isLoading={isSearching}>
      <ModalBody>
        <Title>{t('sellWizard.sfhWizard.shipping.title')}</Title>
        <Body>{t('sellWizard.sfhWizard.shipping.body')}</Body>

        <ToggleRow
          type="button"
          $expanded={isSearchOpen}
          onClick={() => setIsSearchOpen((open) => !open)}
          data-cy="button-sfh-toggle-fedex-search"
        >
          {t('sellWizard.sfhWizard.shipping.checkNearest')}
          <ChevronRight />
        </ToggleRow>

        {isSearchOpen && (
          <SearchSection>
            <ZipLabel htmlFor="sfh-zip-input">
              {t('sellWizard.sfhWizard.shipping.zipLabel')}
            </ZipLabel>
            <SearchRow>
              <ZipInput
                id="sfh-zip-input"
                inputMode="numeric"
                placeholder={t('sellWizard.sfhWizard.shipping.zipPlaceholder')}
                value={zipCode}
                onChange={handleZipChange}
                data-cy="input-sfh-zip"
              />
              <SearchButton
                type="button"
                onClick={handleSearch}
                disabled={zipCode.length !== 5 || isSearching}
                data-cy="button-sfh-search"
              >
                {t('sellWizard.sfhWizard.shipping.search')}
              </SearchButton>
            </SearchRow>
          </SearchSection>
        )}

        {isSearchOpen &&
          locations !== null &&
          (locations.length === 0 ? (
            <EmptyState data-cy="text-sfh-no-locations">
              <InfoIcon />
              <EmptyText>
                {t('sellWizard.sfhWizard.shipping.noResultsTitle')}
                <br />
                {t('sellWizard.sfhWizard.shipping.noResultsBody')}
              </EmptyText>
              <NeedHelpLink href={`tel:${supportPhone.replace(/[^\d+]/g, '')}`}>
                {t('sellWizard.sfhWizard.needHelp')} {supportPhone}
              </NeedHelpLink>
            </EmptyState>
          ) : (
            <ResultsList data-cy="list-sfh-locations">
              {locations.map((location, idx) => (
                // eslint-disable-next-line react/no-array-index-key
                <LocationRow key={`${location.name}-${idx}`}>
                  <LocationName>{location.name}</LocationName>
                  <LocationAddress>{location.address}</LocationAddress>
                </LocationRow>
              ))}
            </ResultsList>
          ))}

        <Spacer />

        <Footer>
          <Button
            variant="primary"
            textTransform="none"
            onClick={handleContinue}
            data-cy="button-sfh-continue-review"
          >
            {t('sellWizard.sfhWizard.shipping.continueToReview')}
          </Button>
        </Footer>
      </ModalBody>
    </SectionLoader>
  );
};

export default SellFromHomeShipping;
